import { LoadingButton } from '@mui/lab'
import { Box, MenuItem, Paper, Select, Stack, TextField } from '@mui/material'
import { SnackbarProvider, enqueueSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { z } from 'zod'
import DashboardErrorView from '../components/DashboardErrorView'
import DashboardLayout from '../components/DashboardLayout'
import DashboardLoadingDataView from '../components/DashboardLoadingDataView'
import Divider from '@mui/material/Divider'
import PersonAdd from '@mui/icons-material/PersonAdd'
import SelectOrgs from '@components/SelectOrgs'
import useForm from '@hooks/useForm'
import API from '@enums/api'
import { describeError } from '@utils/errors'

const defaultStateUser = {
    firstName: '',
    lastName: '',
    email: '',
    orgId: '',
    role: '',
}

const userSchema = z.object({
    firstName: z
        .string()
        .min(3, 'First name must be at least 3 characters.')
        .regex(
            /^[a-zA-Z\s\-']+$/,
            'First name should only contain letters, spaces, apostrophes, and hyphens'
        ),
    lastName: z
        .string()
        .min(3, 'Last name must be at least 3 characters.')
        .regex(
            /^[a-zA-Z\s\-']+$/,
            'Last name should only contain letters, spaces, apostrophes, and hyphens'
        ),
    email: z.string().email('Invalid email address'),
    orgId: z.string(),
    role: z.string().min(1, 'Role cannot be empty'),
})

type UserSchema = z.infer<typeof userSchema>

const AddNewAdminToOrgPage = () => {
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)
    const [success, setSuccess] = useState<boolean>(false)
    const [selectedOrg, setSelectedOrg] = useState<string | undefined>(
        undefined
    )
    const [showForm, setShowForm] = useState(false)
    const [formValid, setFormValid] = useState(false)
    //const [options, setOptions] = useState<OrgDropDownOption[]>([])
    const {
        values: formErrors,
        setValue: setFormError,
        resetForm: resetFormErrors,
    } = useForm<{ [key in keyof Partial<UserSchema>]: string | undefined }>({})

    const [user, setUser] = useState(defaultStateUser)

    useEffect(() => {
        const { error } = userSchema.safeParse(user)
        resetFormErrors()
        if (!error) {
            setFormValid(true)
            return
        }
        const { fieldErrors } = error.formErrors
        for (const field of Object.keys(fieldErrors)) {
            setFormError(
                field as keyof UserSchema,
                fieldErrors[field as keyof typeof fieldErrors]?.[0]
            )
        }
        setFormValid(false)
    }, [user])

    useEffect(() => {
        if (selectedOrg) setUser({ ...user, orgId: selectedOrg })
    }, [selectedOrg])

    if (loading) {
        return <DashboardLoadingDataView loading={loading} />
    }

    if (error) {
        return <DashboardErrorView error={error} loading={loading} />
    }

    if (success) {
        enqueueSnackbar('New admin added successfully', { variant: 'success' })
        setSuccess(false)
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setLoading(true)

        // call the endpoint to add the new admin
        const res = await API.actions.addNewAdminToOrg(user)

        if (!res.ok) {
            setError(describeError(res, 'Failed to add new admin to org'))
            console.error(res)
            setLoading(false)
            return
        }

        setSuccess(true)
        resetForm()
    }

    const resetForm = () => {
        // reset the form
        setUser(defaultStateUser)
        setSelectedOrg(undefined)
        setShowForm(false)
        setLoading(false)
    }

    return (
        <DashboardLayout>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: '64px',
                    mx: 'auto',
                }}
            >
                <Paper
                    style={{
                        paddingInline: 32,
                        paddingBlock: 32,
                        textAlign: 'center',
                        width: '50em',
                    }}
                >
                    <p>Select the Organization to add a new admin to</p>
                    <SelectOrgs
                        //options={options}
                        //setOptions={setOptions}
                        sx={{
                            maxWidth: '30em',
                            mx: 'auto',
                        }}
                        selectedOrg={selectedOrg}
                        setSelectedOrg={setSelectedOrg}
                        setShowForm={setShowForm}
                    />
                    {showForm && selectedOrg !== undefined && (
                        <div
                            style={{
                                display: 'flex',
                                marginTop: 32,
                                justifyContent: 'center',
                                flexDirection: 'column',
                                gap: 16,
                            }}
                        >
                            <Divider variant="middle" />
                            {/* <p>
                                Add a new admin to{' '}
                                {
                                    options.find(
                                        (option) => option.id === selectedOrg
                                    )?.label
                                }
                            </p> */}
                            <form onSubmit={handleSubmit}>
                                <Stack
                                    spacing={2}
                                    direction="row"
                                    sx={{ marginBottom: 4 }}
                                >
                                    <TextField
                                        type="text"
                                        label="First Name"
                                        onChange={(e) =>
                                            setUser({
                                                ...user,
                                                firstName: e.target.value,
                                            })
                                        }
                                        value={user.firstName}
                                        fullWidth
                                        error={!!formErrors.firstName}
                                        helperText={formErrors.firstName}
                                        required
                                    />
                                    <TextField
                                        type="text"
                                        label="Last Name"
                                        onChange={(e) =>
                                            setUser({
                                                ...user,
                                                lastName: e.target.value,
                                            })
                                        }
                                        value={user.lastName}
                                        fullWidth
                                        error={!!formErrors.lastName}
                                        helperText={formErrors.lastName}
                                        required
                                    />
                                </Stack>
                                <Stack
                                    spacing={2}
                                    direction="row"
                                    sx={{ marginBottom: 4 }}
                                >
                                    <TextField
                                        type="email"
                                        label="Email"
                                        onChange={(e) =>
                                            setUser({
                                                ...user,
                                                email: e.target.value,
                                            })
                                        }
                                        value={user.email}
                                        fullWidth
                                        error={!!formErrors.email}
                                        helperText={formErrors.email}
                                        required
                                    />
                                </Stack>
                                <Stack
                                    spacing={2}
                                    direction="row"
                                    sx={{ marginBottom: 4 }}
                                >
                                    <Select
                                        onChange={(e) =>
                                            setUser({
                                                ...user,
                                                role: e.target.value,
                                            })
                                        }
                                        value={user.role}
                                        fullWidth
                                        required
                                        displayEmpty
                                    >
                                        <MenuItem disabled value="">
                                            Select a role
                                        </MenuItem>
                                        <MenuItem value={'admin'}>
                                            Admin
                                        </MenuItem>
                                        <MenuItem value={'atsp'}>
                                            ATSP Admin
                                        </MenuItem>
                                    </Select>
                                </Stack>
                                <LoadingButton
                                    disabled={!formValid}
                                    startIcon={<PersonAdd />}
                                    size="small"
                                    variant="contained"
                                    onClick={handleSubmit}
                                >
                                    Create New Admin User
                                </LoadingButton>
                            </form>
                        </div>
                    )}
                </Paper>
            </Box>
            <SnackbarProvider
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            />
        </DashboardLayout>
    )
}

export default AddNewAdminToOrgPage
